import { FormEvent, useEffect, useState } from "react";
import { evaluationApi, apiClient } from "../api/client";
import { EvaluationCreateInput, User, EvaluationPeriod } from "../api/types";
import { useAuth } from "../context/AuthContext";

const defaultScores = {
  performance: 4,
  behavior: 4,
  skill: 4,
};

function EvaluationForm() {
  const { user: currentUser } = useAuth();
  const [evaluationType, setEvaluationType] = useState<"SELF" | "PEER">("SELF");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  const [scores, setScores] = useState(defaultScores);
  const [comments, setComments] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Data for dropdowns
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [evaluationPeriods, setEvaluationPeriods] = useState<EvaluationPeriod[]>([]);
  const [currentActivePeriod, setCurrentActivePeriod] = useState<EvaluationPeriod | null>(null);

  // Load evaluation periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const response = await apiClient.get<{ periods: EvaluationPeriod[] }>(
          "/evaluation-periods"
        );
        setEvaluationPeriods(response.periods || []);

        // Find current active period
        const activePeriod = response.periods?.find((p: EvaluationPeriod) => p.status === "ACTIVE");
        if (activePeriod) {
          setCurrentActivePeriod(activePeriod);
          setSelectedPeriodId(activePeriod.id);
        }
      } catch (err) {
        console.error("Failed to load periods:", err);
      }
    };
    loadPeriods();
  }, []);

  // Load team members when evaluation type is PEER
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (evaluationType !== "PEER" || !currentUser?.team_id) {
        setTeamMembers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Get team members from the same team (excluding current user)
        const teamUsers = await apiClient.get<User[]>("/users/my-team");
        setTeamMembers(teamUsers);
        // Auto-select first team member
        if (teamUsers.length > 0 && !selectedUserId) {
          setSelectedUserId(teamUsers[0].id);
        }
      } catch (err) {
        console.error("Failed to load team members:", err);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };

    loadTeamMembers();
  }, [evaluationType, currentUser]);

  // Set user ID based on evaluation type
  useEffect(() => {
    if (evaluationType === "SELF" && currentUser) {
      setSelectedUserId(currentUser.id);
    }
  }, [evaluationType, currentUser]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate required fields
    if (!selectedUserId) {
      setStatus("Please select an employee");
      return;
    }
    if (!selectedPeriodId) {
      setStatus("Please select an evaluation period");
      return;
    }
    if (!currentUser?.id) {
      setStatus("User not authenticated");
      return;
    }

    try {
      // Ensure scores is an object with numeric values
      const scoresPayload: Record<string, number> = {};
      for (const [key, value] of Object.entries(scores)) {
        scoresPayload[key] = typeof value === "number" ? value : parseInt(String(value), 10);
      }

      const payload: EvaluationCreateInput = {
        userId: selectedUserId,
        evaluatorId: currentUser.id,
        scores: scoresPayload,
        comments: comments?.trim() || undefined,
        periodId: selectedPeriodId,
        evaluationType: evaluationType,
      };

      console.log("Submitting evaluation payload:", JSON.stringify(payload, null, 2));

      await evaluationApi.create(payload);
      setStatus("Evaluation submitted successfully to D1 database.");
      // Reset form
      setComments("");
      setScores(defaultScores);
    } catch (error) {
      setStatus((error as Error).message);
    }
  };

  if (!currentUser) {
    return <div className="text-slate-600">Please log in to create an evaluation.</div>;
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <p className="text-sm text-slate-500">Individual evaluation</p>
        <h2 className="text-2xl font-semibold text-slate-900">Create evaluation</h2>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4"
      >
        {/* Evaluation Type - shown at top */}
        <div className="space-y-1">
          <label className="text-sm text-slate-700 font-medium">Evaluation Type</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="evaluationType"
                value="SELF"
                checked={evaluationType === "SELF"}
                onChange={(e) => setEvaluationType(e.target.value as "SELF" | "PEER")}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">Self Assessment</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="evaluationType"
                value="PEER"
                checked={evaluationType === "PEER"}
                onChange={(e) => setEvaluationType(e.target.value as "SELF" | "PEER")}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">Peer Review</span>
            </label>
          </div>
        </div>

        {/* Evaluation Period Dropdown */}
        <div className="space-y-1">
          <label className="text-sm text-slate-700 font-medium">Evaluation Period</label>
          <select
            className="w-full rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring focus:ring-brand-accent"
            value={selectedPeriodId}
            onChange={(e) => setSelectedPeriodId(e.target.value)}
            required
          >
            <option value="">Select a period</option>
            {evaluationPeriods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name} ({period.status})
              </option>
            ))}
          </select>
          {currentActivePeriod && (
            <p className="text-xs text-slate-500">
              Current active period: {currentActivePeriod.name}
            </p>
          )}
        </div>

        {/* Employee ID - shown conditionally */}
        {evaluationType === "PEER" ? (
          <div className="space-y-1">
            <label className="text-sm text-slate-700 font-medium">Employee</label>
            {loading ? (
              <div className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-500">
                Loading team members...
              </div>
            ) : teamMembers.length > 0 ? (
              <select
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring focus:ring-brand-accent"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                required
              >
                <option value="">Select a team member</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.email})
                  </option>
                ))}
              </select>
            ) : (
              <div className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-500">
                No team members available
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <label className="text-sm text-slate-700 font-medium">Employee</label>
            <div className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-700 bg-slate-50">
              {currentUser.name} ({currentUser.email})
            </div>
            <p className="text-xs text-slate-500">Self Assessment - you are evaluating yourself</p>
          </div>
        )}

        {/* Scores Grid */}
        <div className="grid md:grid-cols-3 gap-4">
          {Object.keys(scores).map((key) => (
            <div key={key} className="space-y-1">
              <label className="text-sm text-slate-700 capitalize">{key}</label>
              <input
                type="number"
                min={1}
                max={5}
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring focus:ring-brand-accent"
                value={(scores as any)[key]}
                onChange={(e) =>
                  setScores({ ...scores, [key]: Number.parseInt(e.target.value, 10) || 0 })
                }
                required
              />
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <label className="text-sm text-slate-700">Comments</label>
          <textarea
            className="w-full rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring focus:ring-brand-accent"
            rows={4}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className="px-4 py-2 rounded-md bg-brand-primary text-white font-semibold hover:bg-brand-muted transition-colors"
        >
          Submit evaluation
        </button>
        {status && <p className="text-sm text-slate-600">{status}</p>}
      </form>
    </div>
  );
}

export default EvaluationForm;
